import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, brl, formatApiError, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ServiceSuggestInput, SaveAsServiceButton } from "@/components/ServiceSuggest";
import { toast } from "sonner";
import { ShoppingCart, Plus, Trash2, Boxes, Wrench, Loader2 } from "lucide-react";
import { PageHead, Stat, Empty, Field, RangePicker, inputCls, selectCls, btnPrimary, btnGhost, num, monthStartIso, todayIso } from "@/components/erp/ErpUi";
import { ProductPicker } from "@/components/erp/StockDialogs";
import { useProductCosts, ItemProfitLine, BudgetProfitTotals } from "@/components/BudgetProfit";
import { ProfitBoard } from "@/components/ProfitBoard";
import { PaymentDialog } from "@/components/PaymentDialog";
import { maskCpf, maskCnpj, onlyDigits } from "@/components/CustomerProfileFields";

const maskDoc = (v) => (onlyDigits(v).length > 11 ? maskCnpj(v) : maskCpf(v));

function CustomerPick({ value, onChange }) {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/customers").then(({ data }) => setList(data || [])).catch(() => {}); }, []);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return list.filter((c) => !s || (c.name || "").toLowerCase().includes(s) || (c.document || "").includes(onlyDigits(s) || "##") || (c.phone || "").includes(onlyDigits(s) || "##")).slice(0, 50);
  }, [list, q]);
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Button data-testid="sale-walkin-btn" type="button" variant="outline" onClick={() => onChange({ walkin: true, name: "", doc: "" })} className={value.walkin ? "bg-amber-brand text-black font-bold hover:bg-amber-brandDark" : btnGhost}>Consumidor final</Button>
        <Button data-testid="sale-registered-btn" type="button" variant="outline" onClick={() => onChange({ walkin: false, id: "" })} className={!value.walkin ? "bg-amber-brand text-black font-bold hover:bg-amber-brandDark" : btnGhost}>Cliente cadastrado</Button>
      </div>
      {value.walkin ? (
        <div className="grid grid-cols-2 gap-2">
          <Input data-testid="sale-walkin-name" value={value.name || ""} onChange={(e) => onChange({ ...value, name: e.target.value })} placeholder="Nome (opcional)" className={inputCls} />
          <Input data-testid="sale-walkin-doc" value={value.doc || ""} onChange={(e) => onChange({ ...value, doc: maskDoc(e.target.value) })} placeholder="CPF/CNPJ (opcional)" inputMode="numeric" className={inputCls} />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Input data-testid="sale-customer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar nome, CPF ou telefone" className={inputCls} />
          <select data-testid="sale-customer-select" value={value.id || ""} onChange={(e) => onChange({ walkin: false, id: e.target.value })} className={selectCls}>
            <option value="">Selecione o cliente…</option>
            {filtered.map((c) => <option key={c.id} value={c.id}>{c.name}{c.document ? ` · ${maskDoc(c.document)}` : ""}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}

function NewSale({ onCreated }) {
  const { user } = useAuth();
  const [cust, setCust] = useState({ walkin: true, name: "", doc: "" });
  const [items, setItems] = useState([]);
  const [discType, setDiscType] = useState("valor");
  const [disc, setDisc] = useState("");
  const [picker, setPicker] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const subtotal = items.reduce((s, i) => s + num(i.qty) * num(i.unit_price), 0);
  const discValue = discType === "percent" ? subtotal * Math.min(num(disc), 100) / 100 : Math.min(num(disc), subtotal);
  const total = Math.max(0, +(subtotal - discValue).toFixed(2));
  const setItem = (i, k, v) => setItems((p) => p.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const costMap = useProductCosts();
  const itemsForProfit = items.map((it) => ({ product_id: it.product_id, quantity: num(it.qty), unit_price: num(it.unit_price), discount: 0, cost: it.cost }));
  const addProduct = (p) => {
    if (p.stock <= 0) toast.warning("Peça sem estoque: o saldo vai ficar negativo");
    setItems((prev) => [...prev, { product_id: p.id, description: p.description, qty: "1", unit_price: String(p.price), code: p.code }]);
  };
  const finish = async (payment) => {
    if (!items.length) { toast.error("Adicione itens"); return; }
    if (!cust.walkin && !cust.id) { toast.error("Selecione o cliente"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/sales", {
        customer_id: cust.walkin ? null : cust.id, customer_name: cust.walkin ? cust.name : null, customer_doc: cust.walkin ? onlyDigits(cust.doc) : null,
        items: items.map((i) => ({ product_id: i.product_id || null, description: i.description, qty: num(i.qty), unit_price: num(i.unit_price), cost: !i.product_id && i.cost !== "" && i.cost != null ? num(i.cost) : null })),
        discount_type: discType, discount: num(disc), payment: payment || null,
      });
      toast.success(`Venda V${data.number} concluída`); onCreated(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); setPayOpen(false); }
  };
  if (user?.shop_id === "all") return <p className="text-sm text-amber-300">Escolha uma unidade no cabeçalho para vender.</p>;
  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
      <div className="xl:col-span-2 space-y-4">
        <section className="rounded-2xl border border-card2-border bg-card2 p-4"><div className="text-xs font-mono-caps uppercase text-zinc-400 mb-2">Cliente</div><CustomerPick value={cust} onChange={setCust} /></section>
        <section className="rounded-2xl border border-card2-border bg-card2 p-4 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-xs font-mono-caps uppercase text-zinc-400">Itens</div>
            <div className="flex gap-2">
              <Button data-testid="sale-add-product" type="button" variant="outline" className={btnGhost} onClick={() => setPicker(true)}><Boxes className="h-4 w-4 mr-1" /> Peça do estoque</Button>
              <Button data-testid="sale-add-service" type="button" variant="outline" className={btnGhost} onClick={() => setItems((p) => [...p, { description: "", qty: "1", unit_price: "" }])}><Wrench className="h-4 w-4 mr-1" /> Serviço</Button>
            </div>
          </div>
          {items.length === 0 ? <Empty>Nenhum item. Busque a peça por nome, código ou código de barras.</Empty> : items.map((it, i) => (
            <div key={i} data-testid={`sale-item-${i}`} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-12 sm:col-span-6">
                {it.product_id ? <div className="text-sm text-zinc-100">{it.description}<span className="ml-1 text-[11px] text-zinc-500">{it.code}</span></div>
                  : <ServiceSuggestInput testid={`sale-item-${i}-desc`} value={it.description} onChange={(v) => setItem(i, "description", v)}
                      onPick={(s) => setItems((p) => p.map((row, j) => (j === i ? { ...row, description: s.description, code: s.code, unit_price: String(s.unit_price ?? ""), cost: s.cost ?? "" } : row)))}
                      placeholder="Descrição do serviço" className={inputCls} />}
              </div>
              <Input data-testid={`sale-item-${i}-qty`} value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} inputMode="decimal" className={`${inputCls} col-span-3 sm:col-span-2`} />
              <Input data-testid={`sale-item-${i}-price`} value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} inputMode="decimal" placeholder="Preço" className={`${inputCls} col-span-5 sm:col-span-3`} />
              <button type="button" aria-label="Remover" onClick={() => setItems((p) => p.filter((_, j) => j !== i))} className="col-span-4 sm:col-span-1 text-red-400 flex justify-center"><Trash2 className="h-4 w-4" /></button>
              <div className="col-span-12">{!it.product_id && <label className="mb-1 flex items-center gap-2 text-[11px] text-amber-300/80">Custo (R$) <span className="text-zinc-600">só o dono vê</span>
                <Input data-testid={`sale-item-${i}-cost`} value={it.cost ?? ""} onChange={(e) => setItem(i, "cost", e.target.value)} inputMode="decimal" placeholder="0,00" className={`${inputCls} h-8 w-32 bg-amber-950/20 border-amber-900/50`} />
                <span className="ml-auto"><SaveAsServiceButton item={{ description: it.description, code: it.code, unit_price: num(it.unit_price), cost: it.cost }} testid={`sale-item-${i}-save-service`} /></span></label>}
                <ItemProfitLine testid={`sale-item-${i}-profit`} item={{ product_id: it.product_id, quantity: num(it.qty), unit_price: num(it.unit_price), discount: 0, cost: it.cost }} costMap={costMap} /></div>
            </div>
          ))}
        </section>
      </div>
      <section className="rounded-2xl border border-card2-border bg-card2 p-4 space-y-3 h-fit xl:sticky xl:top-4">
        <ProfitBoard items={itemsForProfit} title="Rentabilidade da venda" />
        <div className="flex justify-between text-sm text-zinc-300"><span>Subtotal</span><span>{brl(subtotal)}</span></div>
        <Field label="Desconto">
          <div className="flex gap-2">
            <select data-testid="sale-discount-type" value={discType} onChange={(e) => setDiscType(e.target.value)} className={`${selectCls} w-20`}><option value="valor">R$</option><option value="percent">%</option></select>
            <Input data-testid="sale-discount" value={disc} onChange={(e) => setDisc(e.target.value)} inputMode="decimal" placeholder="0" className={inputCls} />
          </div>
        </Field>
        <div className="flex justify-between items-baseline"><span className="text-xs font-mono-caps uppercase text-zinc-400">Total</span><span data-testid="sale-total" className="font-display text-3xl font-black text-green-neon">{brl(total)}</span></div>
        <Button data-testid="sale-finish-btn" disabled={busy || !items.length} onClick={() => setPayOpen(true)} className={`w-full h-12 ${btnPrimary}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Finalizar venda"}</Button>
        <BudgetProfitTotals testid="sale-profit-totals" items={itemsForProfit} costMap={costMap} />
      </section>
      <ProductPicker open={picker} onOpenChange={setPicker} onPick={addProduct} />
      <PaymentDialog open={payOpen} onOpenChange={setPayOpen} total={total} onConfirm={finish} confirmLabel="Concluir venda" skippable onSkip={() => finish(null)} />
    </div>
  );
}

function SalesList() {
  const [range, setRange] = useState([monthStartIso(), todayIso()]);
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/sales", { params: { from: range[0], to: range[1] } }).then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e))); }, [range]);
  return (
    <div className="space-y-3">
      <RangePicker from={range[0]} to={range[1]} onChange={setRange} testid="sales-range" />
      <div className="grid grid-cols-2 gap-3 max-w-md">
        <Stat testid="sales-count" label="Vendas concluídas" value={data?.summary.count ?? "…"} />
        <Stat testid="sales-total" label="Total vendido" value={data ? brl(data.summary.total) : "…"} tone="green" />
      </div>
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-green-brand mx-auto" /> : data.items.length === 0 ? <Empty>Nenhuma venda no período.</Empty> : (
        <ul className="space-y-2">{data.items.map((s) => (
          <li key={s.id}><Link data-testid={`sale-row-${s.id}`} to={`/owner/balcao/${s.id}`} className="block rounded-xl border border-zinc-800 bg-zinc-950/60 p-3 hover:border-green-brand">
            <div className="flex justify-between gap-2"><span className="font-bold text-zinc-100">V{s.number} · {s.customer_name || "Consumidor final"}</span><span className={`font-display font-black ${s.status === "cancelada" ? "text-zinc-500 line-through" : "text-green-neon"}`}>{brl(s.total)}</span></div>
            <div className="text-[11px] text-zinc-500">{fmtDateTime(s.created_at)} · {s.items.length} item(ns){s.status === "cancelada" ? " · CANCELADA" : ""}{s.payment ? "" : " · pagamento pendente"}</div>
          </Link></li>
        ))}</ul>
      )}
    </div>
  );
}

export default function OwnerCounterSale() {
  const [tab, setTab] = useState("nova");
  const navigate = useNavigate();
  return (
    <div className="space-y-5 animate-fade-up">
      <PageHead icon={ShoppingCart} kicker="Balcão" title="Venda balcão">
        {[["nova", "Nova venda"], ["lista", "Vendas"]].map(([k, l]) => (
          <Button key={k} data-testid={`sale-tab-${k}`} variant="outline" onClick={() => setTab(k)} className={tab === k ? "bg-amber-brand text-black font-bold hover:bg-amber-brandDark" : btnGhost}>{k === "nova" && <Plus className="h-4 w-4 mr-1" />}{l}</Button>
        ))}
      </PageHead>
      {tab === "nova" ? <NewSale onCreated={(s) => navigate(`/owner/balcao/${s.id}?nf=1`)} /> : <SalesList />}
    </div>
  );
}
